import "./attendanceStyle.css";
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import { ToastContainer, toast } from "react-toastify";
import { HiArrowLongLeft } from "react-icons/hi2";
import { RxCross2 } from "react-icons/rx";
import { LuListFilter } from "react-icons/lu";
import { MdOutlineLightMode, MdOutlineDarkMode } from "react-icons/md";

import dayjs from "dayjs";
import {
  getCapitalizeWord,
  getCurrentDate,
  getDateWithMonthName,
  getFormattedMonthlyAttendance,
  splitMonthIntoWeeks,
  getRemainingDatesInMonth,
  isCurrentMonth,
  formatDateWithLongMonth,
  getGreterDate,
} from "../../services/commonServices";
import { fetchBranchData, initBranchState } from "../../redux/slices/branchSlice";
import { fetchDepartmentData, initDepartState } from "../../redux/slices/departmentSlice";
import { fetchData as fetchShiftData, initShiftState } from "../../redux/slices/shiftsSlice";
import { fetchDailyAttData, initDailyAttState } from "../../redux/slices/dailyAttendanceSlice";
import { commonSaveData, initCommonState } from "../../redux/slices/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  dailyAttendancePath,
  employeeIndividualAttendancePath,
  editAbsentAsPresentAttendancePath,
} from "../../services/config";

/* ═══════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════ */
const STATUS_CONFIG = {
  1: { label: "Present", color: "#22c55e", glow: "#22c55e", icon: "✓", grad: ["#22c55e", "#16a34a"] },
  2: { label: "Absent", color: "#f43f5e", glow: "#f43f5e", icon: "✗", grad: ["#f43f5e", "#e11d48"] },
  3: { label: "Half Day", color: "#f59e0b", glow: "#f59e0b", icon: "½", grad: ["#f59e0b", "#d97706"] },
  4: { label: "Week Off", color: "#94a3b8", glow: "#94a3b8", icon: "○", grad: ["#94a3b8", "#64748b"] },
  5: { label: "Holiday", color: "#38bdf8", glow: "#38bdf8", icon: "★", grad: ["#38bdf8", "#0ea5e9"] },
  6: { label: "Paid Leave", color: "#a78bfa", glow: "#a78bfa", icon: "◆", grad: ["#a78bfa", "#7c3aed"] },
  7: { label: "Week Off", color: "#94a3b8", glow: "#94a3b8", icon: "○", grad: ["#94a3b8", "#64748b"] },
  8: { label: "Half Day", color: "#f59e0b", glow: "#f59e0b", icon: "½", grad: ["#f59e0b", "#d97706"] },
  9: { label: "Present", color: "#22c55e", glow: "#22c55e", icon: "✓", grad: ["#22c55e", "#16a34a"] },
};

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FILTER_OPTIONS = [
  { code: "all", label: "All", color: "#94a3b8" },
  { code: "1", label: "Present", color: "#22c55e" },
  { code: "2", label: "Absent", color: "#f43f5e" },
  { code: "3", label: "Half Day", color: "#f59e0b" },
  { code: "4", label: "Week Off", color: "#94a3b8" },
  { code: "5", label: "Holiday", color: "#38bdf8" },
  { code: "6", label: "Paid Leave", color: "#a78bfa" },
];

const FILTER_BUCKET = {
  "1": [1, 9], "2": [2], "3": [3, 8],
  "4": [4, 7], "5": [5], "6": [6],
};

const ATT_TYPES = [
  { val: "1", label: "Present", color: "#22c55e" },
  { val: "2", label: "Absent", color: "#f43f5e" },
  { val: "3", label: "Half Day", color: "#f59e0b" },
  { val: "4", label: "Week Off", color: "#94a3b8" },
  { val: "5", label: "Holiday", color: "#38bdf8" },
];

/* ═══════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════ */

const DayCard = ({ dayData, id, isSelected, isHidden, cfg, onChange }) => {
  const [ripple, setRipple] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRipple({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setTimeout(() => setRipple(null), 600);
  };

  const isRegularized = dayData.isRegularized === true || dayData.isRegularized === "true";

  return (
    <label
      htmlFor={id}
      className={`day-card ${isSelected ? "day-card--selected" : ""} ${isHidden ? "day-card--hidden" : ""} ${mounted ? "day-card--mounted" : ""}`}
      style={{ "--card-color": cfg.color, "--card-glow": cfg.glow }}
      onClick={handleClick}
    >
      {ripple && (
        <span className="day-card__ripple" style={{ left: ripple.x, top: ripple.y }} />
      )}
      <span className="day-card__accent" style={{ background: `linear-gradient(90deg, ${cfg.grad[0]}, ${cfg.grad[1]})` }} />
      {isSelected && (
        <span className="day-card__glow" style={{ background: `radial-gradient(circle at top right, ${cfg.color}40, transparent 70%)` }} />
      )}
      <input type="checkbox" id={id} value={dayData.fullDateWithMonth} onChange={onChange} className="day-card__checkbox" />

      {/* Regularized badge — top left corner */}
      {isRegularized && (
        <span className="day-card__reg-badge" title="Regularized">R</span>
      )}

      <span className="day-card__date" style={{ color: isSelected ? "#fff" : "var(--text-primary)" }}>
        {dayData.date}
      </span>
      {dayData.status && (
        <span className="day-card__label" style={{ color: cfg.color }}>{dayData.status}</span>
      )}
      {dayData.status && (
        <span className="day-card__tooltip">
          {dayData.status}{isRegularized ? " · Regularized" : ""}
        </span>
      )}
    </label>
  );
};

const StatCard = ({ value, label, color, delay = 0 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(0);
    if (!value) return;
    let start = 0;
    const step = Math.ceil(value / 20);
    const t = setInterval(() => {
      start += step;
      if (start >= value) { setCount(value); clearInterval(t); }
      else setCount(start);
    }, 30);
    return () => clearInterval(t);
  }, [value]);

  return (
    <div className="stat-card" style={{ "--stat-color": color, animationDelay: `${delay}ms` }}>
      <div className="stat-card__ring" style={{ borderColor: `${color}30` }} />
      <span className="stat-card__value" style={{ color }}>{count}</span>
      <span className="stat-card__label">{label}</span>
      <div className="stat-card__bar" style={{ background: `${color}20` }}>
        <div className="stat-card__bar-fill" style={{ background: color, width: value > 0 ? "100%" : "0%" }} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const MonthlyAttendance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filterRef = useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const staffId = searchParams.get("staffId");
  const staffName = searchParams.get("staffName");
  const dateParam = searchParams.get("date");
  const initialDate = dateParam ? `${dateParam}-01` : dayjs().format("YYYY-MM-01");

  const user = JSON.parse(localStorage.getItem("user_data")) || null;

  const branchState = useSelector((s) => s.branch);
  const departmentState = useSelector((s) => s.department);
  const shiftState = useSelector((s) => s.shift);
  const dailyAttendanceState = useSelector((s) => s.dailyAttendance);
  const commonStore = useSelector((s) => s.common);

  const [isDark, setIsDark] = useState(true);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [dateArray, setDateArray] = useState([]);
  const [dates, setDates] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAttendanceType, setShowAttendanceType] = useState(false);
  const [editAttValue, setEditAttValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");

  const maxMonth = new Date();

  const summaryCounts = {
    present: dateArray.filter((d) => [1, 9].includes(Number(d.statusCode))).length,
    absent: dateArray.filter((d) => d.statusCode == 2).length,
    halfDay: dateArray.filter((d) => [3, 8].includes(Number(d.statusCode))).length,
    weekOff: dateArray.filter((d) => [4, 7].includes(Number(d.statusCode))).length,
    holiday: dateArray.filter((d) => d.statusCode == 5).length,
    paidLeave: dateArray.filter((d) => d.statusCode == 6).length,
  };

  useEffect(() => {
    const h = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const getAttendanceList = useCallback((sid, selectedDate) => {
    if (!sid || !selectedDate) { toast.error("Missing required parameters"); return; }
    dispatch(commonSaveData({
      path: employeeIndividualAttendancePath,
      data: { userId: user?.user_id, staffId: sid, selectedDate },
    }));
  }, [user?.user_id, dispatch]);

  const getAttendance = useCallback((date, bId = "", dId = "", sId = "") => {
    dispatch(fetchDailyAttData({
      path: dailyAttendancePath,
      data: { userId: user?.user_id, selectedDate: date, branchId: bId, departmentId: dId, shiftId: sId },
    }));
  }, [user?.user_id, dispatch]);

  useEffect(() => {
    if (staffId && initialDate) getAttendanceList(staffId, initialDate);
    else toast.error("Invalid employee data");
  }, [staffId, initialDate, getAttendanceList]);

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchBranchData(user.user_id));
      dispatch(fetchDepartmentData(user.user_id));
      dispatch(fetchShiftData(user.user_id));
    }
  }, [dispatch, user?.user_id]);

  const handleUncheck = () =>
    document.querySelectorAll(".day-card__checkbox").forEach((cb) => (cb.checked = false));

  useEffect(() => {
    if (branchState.brnGetSuccess) dispatch(initBranchState());
    if (departmentState.depGetSuccess) dispatch(initDepartState());
    if (shiftState.shiftGetSuccess) dispatch(initShiftState());

    if (commonStore.commonSuccess) {
      if (selectedValues.length > 0) {
        getAttendanceList(staffId, currentDate);
        setShowAttendanceType(false);
        setSelectedValues([]);
        handleUncheck();
        toast.success(commonStore.commonMessage);
      } else {
        const allDates = commonStore.commonData?.allDates;
        if (allDates?.length) {
          if (isCurrentMonth(currentDate)) {
            const newDates = getRemainingDatesInMonth(allDates);
            setDateArray(newDates);
            setDates(getFormattedMonthlyAttendance(newDates));
            setWeeks(splitMonthIntoWeeks(getFormattedMonthlyAttendance(newDates)));
          } else {
            const lastDate = [allDates[allDates.length - 1]];
            const extra = getRemainingDatesInMonth(lastDate);
            const merged = [...allDates, ...extra];
            const seen = new Set();
            const uniqueData = merged.filter((item) => {
              if (seen.has(item.dates)) return false;
              seen.add(item.dates); return true;
            });
            setDateArray(uniqueData);
            setDates(getFormattedMonthlyAttendance(uniqueData));
            setWeeks(splitMonthIntoWeeks(getFormattedMonthlyAttendance(uniqueData)));
          }
        } else { setDates([]); setWeeks([]); }
      }
      dispatch(initCommonState());
    }

    if (commonStore.commonError) {
      toast.error(commonStore.commonMessage || "Failed to fetch attendance data");
      setSelectedValues([]); setDateArray([]); setDates([]); setWeeks([]);
      dispatch(initCommonState());
    }
  }, [branchState, departmentState, shiftState, commonStore, dispatch, staffId, currentDate, selectedValues.length]);

  useEffect(() => { getAttendance(currentDate); }, [currentDate, getAttendance]);

  const handleDateChange = (value) => {
    setIsOpen(false); setSelectedValues([]);
    if (!value) { toast.error("Invalid date selected"); return; }
    const d = new Date(value);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    setCurrentDate(`${year}-${month}-${day}`);
    getAttendanceList(staffId, `${year}-${month}-${day}`);
  };

  const addDates = (e, id) => {
    const { value, checked } = e.target;
    if (checked) {
      if (getGreterDate(formatDateWithLongMonth(getCurrentDate()), formatDateWithLongMonth(value)) === "date2") {
        toast.error("Future date cannot be selected");
        const el = document.getElementById(id); if (el) el.checked = false;
      } else setSelectedValues((p) => [...p, value]);
    } else setSelectedValues((p) => p.filter((v) => v !== value));
  };

  const saveAttendance = () => {
    if (!editAttValue) { toast.error("Please select attendance type"); return; }
    const absentDates = selectedValues.map((item) => {
      const d = new Date(item);
      return {
        selectedDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      };
    });
    dispatch(commonSaveData({
      path: editAbsentAsPresentAttendancePath,
      data: {
        userId: user?.user_id,
        staffId,
        attendanceType: editAttValue,
        // ✅ Only send clockIn/clockOut if NOT absent
        clockIn: editAttValue !== "2" ? clockIn : "",
        clockOut: editAttValue !== "2" ? clockOut : "",
        absentDates,
      },
    }));
  };

  // ✅ Reset clock fields when attendance type changes to Absent
  const handleAttTypeChange = (val) => {
    setEditAttValue(val);
    if (val === "2") {
      setClockIn("");
      setClockOut("");
    }
  };

  const closePanel = () => {
    setShowAttendanceType(false);
    setEditAttValue("");
    setClockIn("");
    setClockOut("");
  };

  const filteredWeeks = activeFilter === "all"
    ? weeks
    : weeks.map((row) =>
      row.map((cell) => {
        const bucket = FILTER_BUCKET[activeFilter] || [];
        return bucket.includes(Number(cell?.statusCode)) ? cell : (cell ? { ...cell, _hidden: true } : cell);
      })
    );

  const theme = isDark ? "dark" : "light";

  return (
    <div className={`ma-root ma-root--${theme}`}>
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="ma-layout">
        {/* ── LEFT: CALENDAR PANEL ── */}
        <div className="ma-calendar-panel">

          {/* Header */}
          <div className="ma-header">
            <button className="icon-btn" onClick={() => navigate("/attendance#monthly")}>
              <HiArrowLongLeft size={20} />
            </button>

            <div className="ma-picker-wrap">
              <DatePicker
                showMonthYearPicker
                selected={new Date(currentDate)}
                onChange={handleDateChange}
                dateFormat="MMMM yyyy"
                maxDate={maxMonth}
                onClickOutside={() => setIsOpen(false)}
                onInputClick={() => setIsOpen(true)}
                open={isOpen}
                className="ma-datepicker"
              />
            </div>

            {staffName && (
              <div className="staff-badge">
                <span className="staff-badge__avatar">
                  {staffName.charAt(0).toUpperCase()}
                </span>
                <span className="staff-badge__name">{getCapitalizeWord(staffName)}</span>
              </div>
            )}

            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <div ref={filterRef} style={{ position: "relative" }}>
                <button
                  className={`filter-btn ${activeFilter !== "all" ? "filter-btn--active" : ""}`}
                  onClick={() => setShowFilter((p) => !p)}
                >
                  <LuListFilter size={15} />
                  <span>{activeFilter === "all" ? "Filter" : FILTER_OPTIONS.find((f) => f.code === activeFilter)?.label}</span>
                  {activeFilter !== "all" && <span className="filter-dot" />}
                </button>

                {showFilter && (
                  <div className="filter-dropdown">
                    <p className="filter-dropdown__title">Filter by Status</p>
                    {FILTER_OPTIONS.map((opt) => (
                      <div
                        key={opt.code}
                        className={`filter-item ${activeFilter === opt.code ? "filter-item--active" : ""}`}
                        onClick={() => { setActiveFilter(opt.code); setShowFilter(false); }}
                      >
                        <span className="filter-item__dot" style={{ background: opt.color }} />
                        <span>{opt.label}</span>
                        {activeFilter === opt.code && <span className="filter-item__check">✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          {dates?.length > 0 && (
            <div className="stats-grid">
              <StatCard value={summaryCounts.present} label="Present" color="#22c55e" delay={0} />
              <StatCard value={summaryCounts.absent} label="Absent" color="#f43f5e" delay={60} />
              <StatCard value={summaryCounts.halfDay} label="Half Day" color="#f59e0b" delay={120} />
              <StatCard value={summaryCounts.weekOff} label="Week Off" color="#94a3b8" delay={180} />
              <StatCard value={summaryCounts.holiday} label="Holiday" color="#38bdf8" delay={240} />
              <StatCard value={summaryCounts.paidLeave} label="Paid Leave" color="#a78bfa" delay={300} />
            </div>
          )}

          {/* Day headers */}
          {dates?.length > 0 && (
            <div className="day-headers">
              {DAYS_OF_WEEK.map((d) => (
                <div key={d} className="day-header">{d}</div>
              ))}
            </div>
          )}

          {/* Calendar grid */}
          <div className="cal-scroll">
            {dates?.length > 0 ? (
              <table className="cal-table">
                <tbody>
                  {filteredWeeks.map((row, rowIdx) => {
                    const byDay = (row || []).reduce((acc, item) => {
                      if (item) acc[item.day] = item; return acc;
                    }, {});
                    return (
                      <tr key={rowIdx}>
                        {DAYS_OF_WEEK.map((d) => {
                          const cell = byDay[d];
                          if (!cell) return <td key={d} className="cal-cell" />;
                          const cfg = STATUS_CONFIG[cell.statusCode] || { label: "–", color: "#94a3b8", glow: "#94a3b8", icon: "?", grad: ["#94a3b8", "#64748b"] };
                          const id = `cell-${rowIdx}-${d}`;
                          return (
                            <td key={d} className="cal-cell">
                              {cell.statusCode === "0" ? (
                                <div className="day-card day-card--empty">
                                  <span className="day-card__date day-card__date--muted">{cell.date}</span>
                                </div>
                              ) : (
                                <DayCard
                                  dayData={cell}
                                  id={id}
                                  isSelected={selectedValues.includes(cell.fullDateWithMonth)}
                                  isHidden={!!cell._hidden}
                                  cfg={cfg}
                                  onChange={(e) => addDates(e, id)}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <div className="empty-state__orb" />
                <span className="empty-state__icon">📅</span>
                <p className="empty-state__title">No records found</p>
                <p className="empty-state__sub">Try selecting a different month</p>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: SIDEBAR ── */}
        <div className="ma-sidebar">
          {selectedValues.length > 0 ? (
            <div className="sidebar-content">
              <div className="sidebar-header">
                <span className="sidebar-header__title">Selected</span>
                <span className="sidebar-header__badge">{selectedValues.length}</span>
              </div>
              <ul className="dates-list">
                {[...selectedValues]
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((date, i) => (
                    <li key={i} className="date-item" style={{ animationDelay: `${i * 40}ms` }}>
                      <span className="date-item__dot" />
                      <span>{date}</span>
                    </li>
                  ))}
              </ul>
              <div className="sidebar-footer">
                <button className="set-att-btn" onClick={() => setShowAttendanceType(true)}>
                  <span>Set Attendance</span>
                  <span className="set-att-btn__arrow">→</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="sidebar-empty">
              <div className="sidebar-empty__orb" />
              <span className="sidebar-empty__icon">📌</span>
              <p className="sidebar-empty__title">Select Dates</p>
              <p className="sidebar-empty__sub">Tap day cards to assign attendance</p>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT SLIDE PANEL ── */}
      {showAttendanceType && <div className="panel-overlay" onClick={closePanel} />}
      <div className={`att-panel ${showAttendanceType ? "att-panel--open" : ""}`}>

        <div className="att-panel__topbar">
          <button className="att-panel__close" onClick={closePanel}><RxCross2 size={16} /></button>
          <div className="att-panel__title-wrap">
            <span className="att-panel__title">Edit Attendance</span>
            <span className="att-panel__date">{currentDate && getDateWithMonthName(currentDate)}</span>
          </div>
          <div className="att-panel__staff">{getCapitalizeWord(staffName)}</div>
        </div>

        <div className="att-panel__section-label">Selected Dates ({selectedValues.length})</div>
        <div className="att-panel__dates-scroll">
          {[...selectedValues].sort((a, b) => new Date(a) - new Date(b)).map((date, i) => (
            <div key={i} className="att-panel__date-chip" style={{ animationDelay: `${i * 30}ms` }}>
              <span className="att-panel__date-chip-dot" />{date}
            </div>
          ))}
        </div>

        <div className="att-panel__divider" />
        <div className="att-panel__section-label">Attendance Type</div>

        <div className="att-type-grid">
          {ATT_TYPES.map(({ val, label, color }) => (
            <button
              key={val}
              onClick={() => handleAttTypeChange(val)}  
              className={`att-type-chip ${editAttValue === val ? "att-type-chip--active" : ""}`}
              style={{ "--chip-color": color }}
            >
              {editAttValue === val && (
                <span className="att-type-chip__dot" style={{ background: color }} />
              )}
              <span className="att-type-chip__label">{label}</span>
            </button>
          ))}
        </div>

        {/* ✅ FIX 1: Hide Clock In / Out when Absent ("2") is selected */}
        {editAttValue !== "2" && (
          <>
            <div className="att-panel__divider" />
            <div className="att-panel__section-label">Time (Optional)</div>
            <div className="att-panel__time-row">
              <div className="att-panel__time-field">
                <label className="att-panel__time-label">Clock In</label>
                <input
                  type="time"
                  className="att-panel__time-input"
                  value={clockIn}
                  onChange={(e) => setClockIn(e.target.value)}
                />
              </div>
              <span className="att-panel__time-arrow">→</span>
              <div className="att-panel__time-field">
                <label className="att-panel__time-label">Clock Out</label>
                <input
                  type="time"
                  className="att-panel__time-input"
                  value={clockOut}
                  onChange={(e) => setClockOut(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        <div className="att-panel__footer">
          <button
            className={`att-panel__update-btn ${editAttValue ? "att-panel__update-btn--ready" : ""}`}
            onClick={saveAttendance}
            disabled={!editAttValue}
          >
            {editAttValue ? "Update Attendance" : "Select a type to continue"}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════
          GLOBAL STYLES
      ══════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
.ma-root--dark {
  --bg-base:        #0c0a1a;
  --bg-panel:       #19152C;
  --bg-surface:     #100D1B;
  --bg-card:        #19152C;
  --bg-card-hover:  #131022;
  --border:         #2D2A45;
  --border-strong:  #3D3A58;
  --text-primary:   #E3E2EB;
  --text-secondary: #9998A2;
  --text-muted:     #5c5882;
  --shadow:         rgba(0,0,0,0.5);
  --overlay:        rgba(0,0,0,0.7);
  --scrollbar:      #2D2A45;
}

.ma-root--light {
  --bg-base:        #F5F7FA;
  --bg-panel:       #FFFFFF;
  --bg-surface:     #F8FAFF;
  --bg-card:        #FFFFFF;
  --bg-card-hover:  #FFFFFF;
  --border:         #E0E0F0;
  --border-strong:  #E0E0F0;
  --text-primary:   #1A1A2E;
  --text-secondary: #6B6B8A;
  --text-muted:     #9996c0;
  --shadow:         rgba(100,90,200,0.12);
  --overlay:        rgba(20,15,60,0.5);
  --scrollbar:      #E0E0F0;
}

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .ma-root {
          position: fixed;
          inset: 0;
          top: 60px;
          background: var(--bg-base);
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.3s ease, color 0.3s ease;
        }

        .ma-layout {
          display: flex;
          height: 100%;
          width: 100%;
          overflow: hidden;
        }

        /* ─── Calendar Panel ─── */
        .ma-calendar-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 20px 24px;
          min-width: 0;
          background: var(--bg-panel);
        }

        /* ─── Header ─── */
        .ma-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          flex-shrink: 0;
        }

        .icon-btn {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: var(--text-secondary);
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .icon-btn:hover {
          background: var(--bg-card-hover);
          color: var(--text-primary);
          border-color: var(--border-strong);
          transform: translateY(-1px);
        }

        .ma-picker-wrap { position: relative; }

        .ma-datepicker {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 8px 16px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ma-datepicker:hover {
          border-color: var(--border-strong);
          background: var(--bg-card-hover);
        }

        .staff-badge {
          display: flex; align-items: center; gap: 8px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 4px 12px 4px 4px;
        }
        .staff-badge__avatar {
          width: 30px; height: 30px;
          background: linear-gradient(135deg, #7c5cbf, #4f46e5);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; color: #fff;
        }
        .staff-badge__name {
          font-size: 13px; font-weight: 600;
          color: var(--text-primary);
        }

        .filter-btn {
          display: flex; align-items: center; gap: 7px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 8px 14px;
          color: var(--text-secondary);
          cursor: pointer; font-size: 13px; font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.2s;
          position: relative;
        }
        .filter-btn:hover, .filter-btn--active {
          background: var(--bg-card-hover);
          color: var(--text-primary);
          border-color: var(--border-strong);
        }
        .filter-dot {
          position: absolute; top: 5px; right: 5px;
          width: 6px; height: 6px;
          background: #3b82f6; border-radius: 50%;
        }
        .filter-dropdown {
          position: absolute; top: calc(100% + 8px); right: 0;
          min-width: 180px;
          background: var(--bg-card);
          border: 1px solid var(--border-strong);
          border-radius: 12px;
          padding: 6px 0; z-index: 1000;
          box-shadow: 0 8px 32px var(--shadow);
          animation: fadeSlideDown 0.15s ease;
        }
        .filter-dropdown__title {
          font-size: 10px; text-transform: uppercase;
          letter-spacing: 0.6px; color: var(--text-muted);
          padding: 8px 14px 4px; margin: 0;
        }
        .filter-item {
          display: flex; align-items: center; gap: 9px;
          padding: 8px 14px; cursor: pointer;
          color: var(--text-secondary); font-size: 13px;
          transition: all 0.15s;
        }
        .filter-item:hover { background: var(--bg-card-hover); color: var(--text-primary); }
        .filter-item--active { background: var(--bg-surface); color: var(--text-primary); }
        .filter-item__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .filter-item__check { margin-left: auto; color: #3b82f6; font-weight: 700; }

        /* ─── Stats Grid ─── */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 10px;
          margin-bottom: 20px;
          flex-shrink: 0;
        }

        .stat-card {
          position: relative;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 12px 8px 10px;
          text-align: center;
          overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s;
          animation: statFadeIn 0.4s ease both;
        }
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px var(--shadow);
          border-color: var(--stat-color);
        }
        .stat-card__ring {
          position: absolute; top: -20px; right: -20px;
          width: 60px; height: 60px;
          border-radius: 50%; border: 8px solid; opacity: 0.4;
        }
        .stat-card__value {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 26px; font-weight: 700;
          display: block; line-height: 1;
          transition: color 0.3s;
        }
        .stat-card__label {
          font-size: 10px; font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase; letter-spacing: 0.4px;
          margin-top: 6px; display: block;
        }
        .stat-card__bar {
          height: 3px; border-radius: 2px;
          margin: 8px 4px 0; overflow: hidden;
        }
        .stat-card__bar-fill {
          height: 100%; border-radius: 2px;
          transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* ─── Day Headers ─── */
        .day-headers {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px; margin-bottom: 6px; flex-shrink: 0;
        }
        .day-header {
          text-align: center; font-size: 10px; font-weight: 700;
          color: var(--text-muted); text-transform: uppercase;
          letter-spacing: 0.5px; padding: 4px 0;
        }

        /* ─── Calendar Grid ─── */
        .cal-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; }
        .cal-table { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 4px; }
        .cal-cell { padding: 2px; vertical-align: top; }

        /* ══ Day Card ══ */
        .day-card {
          position: relative;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: var(--bg-card);
          padding: 6px 3px 5px;
          cursor: pointer;
          min-height: 78px;
          overflow: hidden;
          transition: transform 0.18s ease,
                      box-shadow 0.18s ease,
                      border-color 0.18s ease,
                      background 0.18s ease;
          opacity: 0;
        }
        .day-card--mounted { opacity: 1; animation: cardPop 0.25s ease both; }
        .day-card:hover:not(.day-card--empty) {
          transform: translateY(-3px);
          /* ✅ FIX 2: light border glow on hover/check using card color at low opacity */
          box-shadow: 0 6px 18px var(--shadow), 0 0 0 1px color-mix(in srgb, var(--card-color, #888) 40%, transparent);
          border-color: color-mix(in srgb, var(--card-color, var(--border-strong)) 50%, transparent);
          background: var(--bg-card-hover);
          z-index: 2;
        }
        .day-card--selected {
          transform: translateY(-2px) !important;
          /* ✅ FIX 2: soft light border when day is selected (checked) */
          border-color: color-mix(in srgb, var(--card-color) 55%, transparent) !important;
          border-width: 1.5px !important;
          box-shadow:
            0 0 0 2px color-mix(in srgb, var(--card-color) 20%, transparent),
            0 4px 20px color-mix(in srgb, var(--card-glow) 25%, transparent) !important;
        }
        .day-card--hidden { opacity: 0.15 !important; pointer-events: none; }
        .day-card--empty { pointer-events: none; opacity: 0.25; border-style: dashed; }

        .day-card__ripple {
          position: absolute;
          width: 4px; height: 4px;
          background: var(--card-color, rgba(255,255,255,0.4));
          border-radius: 50%;
          transform: translate(-50%, -50%) scale(0);
          animation: rippleAnim 0.6s ease-out forwards;
          pointer-events: none; opacity: 0.6;
        }
        .day-card__accent {
          position: absolute; top: 0; left: 0; right: 0;
          height: 3px; border-radius: 12px 12px 0 0;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .day-card--selected .day-card__accent {
          opacity: 1;
        }
        .day-card__glow {
          position: absolute; inset: 0;
          pointer-events: none; border-radius: 12px;
        }
        .day-card__icon {
          width: 26px; height: 26px;
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800;
          margin-bottom: 5px;
          transition: all 0.18s ease;
          flex-shrink: 0;
        }
        .day-card__checkbox { position: absolute; opacity: 0; width: 0; height: 0; }
        .day-card__date {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 17px; font-weight: 700;
          color: var(--text-primary);
          transition: color 0.2s;
          line-height: 1;
        }
        .day-card__date--muted { color: var(--text-muted); font-size: 15px; }
        .day-card__label {
          font-size: 8px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.3px;
          margin-top: 3px;
        }
        .day-card__tooltip {
          position: absolute; bottom: calc(100% + 8px);
          left: 50%; transform: translateX(-50%);
          background: var(--bg-card);
          border: 1px solid var(--border-strong);
          color: var(--text-primary);
          font-size: 11px; font-weight: 600;
          padding: 4px 10px; border-radius: 6px;
          white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity 0.15s;
          z-index: 10;
          box-shadow: 0 4px 12px var(--shadow);
        }
        .day-card:hover .day-card__tooltip { opacity: 1; }

        /* ─── Sidebar ─── */
        .ma-sidebar {
          width: 256px; flex-shrink: 0;
          border-left: 1px solid var(--border);
          background: var(--bg-panel);
          display: flex; flex-direction: column;
        }
        .sidebar-content { display: flex; flex-direction: column; height: 100%; }
        .sidebar-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid var(--border);
        }
        .sidebar-header__title {
          font-size: 13px; font-weight: 700;
          color: var(--text-primary); text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .sidebar-header__badge {
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          color: #fff; font-size: 12px; font-weight: 700;
          padding: 2px 10px; border-radius: 20px;
        }
        .dates-list {
          flex: 1; list-style: none;
          padding: 12px; margin: 0; overflow-y: auto;
        }
        .date-item {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 10px;
          color: var(--text-secondary); font-size: 12px;
          border-radius: 8px;
          transition: background 0.15s;
          animation: fadeSlideRight 0.25s ease both;
        }
        .date-item:hover { background: var(--bg-card-hover); color: var(--text-primary); }
        .date-item__dot {
          width: 6px; height: 6px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          border-radius: 50%; flex-shrink: 0;
        }
        .sidebar-footer { padding: 14px; border-top: 1px solid var(--border); }
        .set-att-btn {
          width: 100%;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          border: none; border-radius: 12px;
          padding: 12px 16px;
          color: #fff; font-weight: 700; font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          display: flex; align-items: center; justify-content: space-between;
          transition: all 0.2s;
          box-shadow: 0 4px 16px rgba(79,70,229,0.35);
        }
        .set-att-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(79,70,229,0.45); }
        .set-att-btn__arrow { font-size: 18px; transition: transform 0.2s; }
        .set-att-btn:hover .set-att-btn__arrow { transform: translateX(4px); }

        .sidebar-empty {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          height: 100%; text-align: center; padding: 20px;
          position: relative; overflow: hidden;
        }
        .sidebar-empty__orb {
          position: absolute;
          width: 180px; height: 180px;
          background: radial-gradient(circle, rgba(79,70,229,0.1), transparent 70%);
          border-radius: 50%;
          animation: orbPulse 3s ease-in-out infinite;
        }
        .sidebar-empty__icon { font-size: 44px; opacity: 0.4; margin-bottom: 14px; z-index: 1; }
        .sidebar-empty__title { color: var(--text-primary); font-size: 14px; font-weight: 700; margin-bottom: 6px; z-index: 1; }
        .sidebar-empty__sub { color: var(--text-muted); font-size: 12px; z-index: 1; }

        /* ════════════════════════════════════
           RIGHT SLIDE PANEL (Edit Attendance)
        ════════════════════════════════════ */

        .panel-overlay {
          position: fixed; inset: 0;
          background: var(--overlay);
          backdrop-filter: blur(3px);
          z-index: 9998;
          animation: overlayIn 0.25s ease both;
        }

        .att-panel {
          position: fixed;
          top: 60px; right: 0; bottom: 0;
          width: 340px;
          background: var(--bg-card);
          border-left: 1px solid var(--border-strong);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }
        .att-panel--open {
          transform: translateX(0);
        }

        .att-panel__topbar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 18px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
        }
        .att-panel__close {
          width: 30px; height: 30px;
          display: flex; align-items: center; justify-content: center;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 0;
          cursor: pointer;
          color: var(--text-secondary);
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .att-panel__close:hover {
          background: var(--bg-card-hover);
          color: var(--text-primary);
          border-color: var(--border-strong);
        }
        .att-panel__title-wrap {
          display: flex; flex-direction: column; gap: 2px; flex: 1;
        }
        .att-panel__title {
          font-size: 14px; font-weight: 700;
          color: var(--text-primary);
          font-family: 'Space Grotesk', sans-serif;
        }
        .att-panel__date {
          font-size: 11px; color: var(--text-muted);
        }
        .att-panel__staff {
          font-size: 12px; font-weight: 600;
          color: var(--text-secondary);
          background: var(--bg-surface);
          border: 1px solid var(--border);
          padding: 4px 10px;
          border-radius: 0;
          flex-shrink: 0;
        }

        .att-panel__section-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: var(--text-primary);
          padding: 14px 18px 6px;
          flex-shrink: 0;
        }

        .att-panel__dates-scroll {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 0 18px 12px;
          max-height: 90px;
          overflow-y: auto;
          flex-shrink: 0;
        }
        .att-panel__date-chip {
          display: flex; align-items: center; gap: 6px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 0;
          padding: 4px 10px;
          font-size: 11px; font-weight: 500;
          color: var(--text-secondary);
          animation: fadeSlideRight 0.2s ease both;
        }
        .att-panel__date-chip-dot {
          width: 5px; height: 5px;
          background: #4f46e5;
          border-radius: 50%; flex-shrink: 0;
        }

        .att-panel__divider {
          height: 1px;
          background: var(--border);
          margin: 0 18px;
          flex-shrink: 0;
        }

        .att-type-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          padding: 8px 18px 14px;
          flex-shrink: 0;
        }
        .att-type-chip {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 6px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.18s ease;
          overflow: hidden;
        }
        .att-type-chip:hover {
          border-color: var(--chip-color);
          background: var(--bg-card-hover);
        }
        .att-type-chip--active {
          border-color: var(--chip-color) !important;
          border-width: 2px !important;
          background: var(--bg-card-hover) !important;
        }
        .att-type-chip__dot {
          position: absolute;
          top: 6px; right: 6px;
          width: 6px; height: 6px;
          border-radius: 50%;
        }
        .att-type-chip__label {
          font-size: 12px; font-weight: 600;
          color: var(--text-primary);
          text-align: center;
        }
        .att-type-chip--active .att-type-chip__label {
          color: var(--chip-color);
        }

        .att-panel__footer {
          padding: 16px 18px;
          border-top: 1px solid var(--border);
          flex-shrink: 0;
        }
        .att-panel__update-btn {
          width: 100%;
          padding: 13px 0;
          border: none;
          border-radius: 0;
          font-size: 14px; font-weight: 700;
          font-family: 'DM Sans', sans-serif;
          cursor: not-allowed;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          color: var(--text-muted);
          letter-spacing: 0.3px;
          transition: all 0.25s;
        }
        .att-panel__update-btn--ready {
          cursor: pointer;
          background: #4f46e5;
          border-color: #4f46e5;
          color: #fff;
        }
        .att-panel__update-btn--ready:hover {
          background: #4338ca;
          border-color: #4338ca;
          transform: translateY(-1px);
        }

        /* ─── Clock In / Out ─── */
        .att-panel__time-row {
          display: flex;
          align-items: flex-end;
          gap: 10px;
          padding: 8px 18px 16px;
          flex-shrink: 0;
        }
        .att-panel__time-field {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .att-panel__time-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-primary);
          font-weight: 600;
        }

        /* ─── Regularized Badge ─── */
        .day-card__reg-badge {
          position: absolute;
          top: 5px; left: 5px;
          width: 14px; height: 14px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #fff;
          font-size: 7px; font-weight: 800;
          border-radius: 3px;
          display: flex; align-items: center; justify-content: center;
          letter-spacing: 0px;
          z-index: 2;
          box-shadow: 0 1px 4px rgba(245,158,11,0.4);
        }
        .att-panel__time-input {
          width: 100%;
          padding: 10px 12px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: border-color 0.2s;
          color-scheme: dark;
        }
        .att-panel__time-input:focus {
          border-color: #4f46e5;
        }
        .att-panel__time-arrow {
          font-size: 16px;
          color: var(--text-muted);
          padding-bottom: 10px;
          flex-shrink: 0;
        }

        /* ─── Empty State ─── */
        .empty-state {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          height: 300px; position: relative;
        }
        .empty-state__orb {
          position: absolute;
          width: 220px; height: 220px;
          background: radial-gradient(circle, rgba(79,70,229,0.08), transparent 70%);
          border-radius: 50%;
          animation: orbPulse 3s ease-in-out infinite;
        }
        .empty-state__icon { font-size: 56px; opacity: 0.4; margin-bottom: 16px; z-index: 1; }
        .empty-state__title { color: var(--text-primary); font-size: 16px; font-weight: 600; margin-bottom: 6px; z-index: 1; }
        .empty-state__sub { color: var(--text-muted); font-size: 13px; z-index: 1; }

        /* ─── Scrollbar ─── */
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--scrollbar); border-radius: 3px; }

        /* ─── Animations ─── */
        @keyframes cardPop {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes rippleAnim {
          to { transform: translate(-50%,-50%) scale(50); opacity: 0; }
        }
        @keyframes statFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideRight {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes orbPulse {
          0%, 100% { transform: scale(1);   opacity: 0.6; }
          50%       { transform: scale(1.15); opacity: 1;   }
        }
        @keyframes overlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MonthlyAttendance;